/**
 * Ready for the tech (#1728, Finish Line slice 4): the soft pre-flight check
 * on dispatch.
 *
 * When someone with the new job page switched on schedules, dispatches or
 * assigns a job that hasn't been dispatched yet, and the job's before-visit
 * promises aren't all kept, they get one confirm: "3 things aren't ready for
 * the tech: … Dispatch anyway?" with [Fix first] [Dispatch anyway].
 *
 * It is a WARNING, never a gate: "Dispatch anyway" goes ahead exactly as it
 * would have, the server is unchanged, and any failure to read the Finish Line
 * lets the dispatch through (a broken read must never stop a booking). With
 * the switch off nothing here runs.
 */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { finishLineKey, useFinishLineSwitch, type FinishLine, type JobPromise } from "@/lib/finish-line";

/**
 * The before-visit promises that are about BOOKING the visit, not preparing
 * it. Booking is the very act being confirmed, so they never count as "not
 * ready".
 */
const BOOKING_PROMISES = new Set(["visit_booked", "booking_confirmed"]);
/** Waiting counts: parts still on order are the vendor's move, but the tech
 *  still can't do the job without them. */
const NOT_READY_STATES = new Set<JobPromise["state"]>(["open", "blocked", "missed", "waiting"]);

/** The preparation the tech is still owed, in catalogue order. */
export function notReadyForTech(fl: FinishLine | null | undefined): JobPromise[] {
    if (!fl) return [];
    return fl.promises.filter(
        (p) => p.phase === "before_visit" && NOT_READY_STATES.has(p.state) && !BOOKING_PROMISES.has(p.id),
    );
}

/** "3 things aren't ready for the tech:" plus one line per promise. */
export function preflightMessage(list: JobPromise[]): string {
    const n = list.length;
    const head = n === 1 ? "1 thing isn't ready for the tech:" : `${n} things aren't ready for the tech:`;
    const lines = list.map((p) => `• ${p.title}: ${p.detail}`);
    return [head, ...lines, "", "Dispatch anyway?"].join("\n");
}

/** The job fields that say whether it has been dispatched yet. */
export interface PreflightJob {
    status: string;
    scheduled_start?: string | null;
    assigned_to?: string | null;
    arrived_at?: string | null;
    work_started_at?: string | null;
    completed_at?: string | null;
}

/**
 * Whether a schedule / assign on this job is a DISPATCH (the job hasn't been
 * sent to a tech yet), as opposed to moving or re-pointing a job that already
 * was. Moving a dispatched job doesn't re-ask: the office already decided.
 * Same pre-visit test as the job page's "Pre-flight" label.
 */
export function isDispatch(job: PreflightJob | null | undefined): boolean {
    if (!job) return false;
    const preVisitStatus = ["draft", "awaiting_scheduling", "scheduled", "unqueued"].includes(job.status);
    const heldBefore = job.status === "held" && !job.arrived_at && !job.work_started_at && !job.completed_at;
    if (!preVisitStatus && !heldBefore) return false;
    return job.status !== "scheduled" || !job.scheduled_start || !job.assigned_to;
}

export const PREFLIGHT_TITLE = "Not ready for the tech";

/**
 * `confirmDispatch(jobId, job)` resolves true to go ahead, false when the
 * office picked "Fix first". It asks only when the switch is on, the change is
 * a dispatch, and something before the visit is still open.
 */
export function useDispatchPreflight(opts?: {
    /** #1766: the new Grid shows readiness in the tray, so its drops run the
     *  same check even when the new job page switch is off. Still only for
     *  people who can read the Finish Line. */
    alsoWhen?: boolean;
}): {
    active: boolean;
    confirmDispatch: (jobId: string, job: PreflightJob | null | undefined) => Promise<boolean>;
} {
    const qc = useQueryClient();
    const sw = useFinishLineSwitch();
    const active = sw.on || (opts?.alsoWhen === true && sw.allowed);
    const confirmDispatch = useCallback(
        async (jobId: string, job: PreflightJob | null | undefined) => {
            if (!active || !isDispatch(job)) return true;
            let fl: FinishLine;
            try {
                fl = await qc.fetchQuery({
                    queryKey: finishLineKey(jobId),
                    queryFn: async () =>
                        (await api.get<{ finishLine: FinishLine }>(`/api/jobs/${jobId}/finish-line`)).finishLine,
                    staleTime: 15_000,
                    retry: false,
                });
            } catch {
                // A warning that can't load is no warning: let it through.
                return true;
            }
            const list = notReadyForTech(fl);
            if (list.length === 0) return true;
            return confirmDialog({
                title: PREFLIGHT_TITLE,
                message: preflightMessage(list),
                confirmLabel: "Dispatch anyway",
                cancelLabel: "Fix first",
                focusCancel: true,
            });
        },
        [active, qc],
    );
    return { active, confirmDispatch };
}
